/**
 * API service for account-level extensions (callouts, sitelinks, structured snippets).
 *
 * Account-level extensions are CustomerAsset resources that apply across all campaigns.
 * This supports the RMF compliance requirement for "Callout extensions at account level".
 */
import apiClient from './client';

// =============================================================================
// Types
// =============================================================================

export type AccountExtensionType = 'callout' | 'sitelink' | 'structured_snippet';
export type AccountExtensionStatus = 'ENABLED' | 'PAUSED' | 'REMOVED';
export type AccountExtensionAction = 'enable' | 'pause' | 'remove';

export interface AccountExtension {
  asset_id: string;
  resource_name?: string;
  text: string;
  asset_type: AccountExtensionType;
  field_type: string;
  status: AccountExtensionStatus;
  google_ads_account_id?: string;
  // Sitelink-specific fields
  final_url?: string;
  description1?: string;
  description2?: string;
  // Structured snippet-specific fields
  header?: string;
  values?: string[];
}

export interface GetAccountExtensionsRequest {
  member_uuid?: string;  // Deprecated: backend uses authenticated user
  customer_id: string;
  asset_type?: AccountExtensionType;
}

export interface GetAccountExtensionsResponse {
  success: boolean;
  customer_id: string;
  assets: AccountExtension[];
  counts: {
    callout: number;
    sitelink: number;
    structured_snippet: number;
  };
  error?: string;
}

export interface SaveAccountExtensionRequest {
  member_uuid?: string;  // Deprecated: backend uses authenticated user
  customer_id: string;
  asset_type: AccountExtensionType;
  text: string;
  // Sitelink-specific fields
  final_url?: string;
  description1?: string;
  description2?: string;
  // Structured snippet-specific fields
  header?: string;
  values?: string[];
  trigger_data_sync?: boolean;
}

export interface SaveAccountExtensionResponse {
  success: boolean;
  asset_id?: string;
  asset_type?: AccountExtensionType;
  text?: string;
  data_sync_triggered: boolean;
  error?: string;
}

export interface UpdateAccountExtensionStatusRequest {
  member_uuid?: string;  // Deprecated: backend uses authenticated user
  customer_id: string;
  asset_ids: string[];
  action: AccountExtensionAction;
  trigger_data_sync?: boolean;
}

export interface UpdateAccountExtensionStatusResponse {
  success: boolean;
  updated_count: number;
  error_count: number;
  partial_success: boolean;  // True when some but not all updates succeeded
  data_sync_triggered: boolean;
  errors: Array<{ asset_id?: string; error?: string }>;
  error?: string;
}

// Note: STRUCTURED_SNIPPET_HEADERS is defined in @/components/features/AccountExtensions/types.ts
// with title-cased values (e.g., "Service catalog") that match the backend API expectations.
// Import from there if needed: import { STRUCTURED_SNIPPET_HEADERS } from '@/components/features/AccountExtensions/types';

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get account-level extensions (callouts, sitelinks, structured snippets).
 *
 * These are CustomerAsset resources that apply across all campaigns in the account.
 */
export async function getAccountExtensions(
  request: GetAccountExtensionsRequest
): Promise<GetAccountExtensionsResponse> {
  const response = await apiClient.post<GetAccountExtensionsResponse>(
    '/account-extensions/list',
    request
  );
  return response.data;
}

/**
 * Create a new account-level extension.
 *
 * Supported types: 'callout', 'sitelink', 'structured_snippet'
 * - Callout: text only (max 25 chars)
 * - Sitelink: text (max 25 chars) + final_url (required) + optional description1/description2 (max 35 chars each)
 * - Structured Snippet: header (from predefined list) + values array (up to 10 values, max 25 chars each)
 */
export async function saveAccountExtension(
  request: SaveAccountExtensionRequest
): Promise<SaveAccountExtensionResponse> {
  const response = await apiClient.post<SaveAccountExtensionResponse>(
    '/account-extensions/save',
    {
      ...request,
      trigger_data_sync: request.trigger_data_sync ?? true,
    },
    {
      timeout: 60000, // 1 minute for Google Ads API
    }
  );
  return response.data;
}

/**
 * Update status of account-level extensions (enable, pause, or remove).
 *
 * Supports batch operations on multiple asset IDs.
 */
export async function updateAccountExtensionStatus(
  request: UpdateAccountExtensionStatusRequest
): Promise<UpdateAccountExtensionStatusResponse> {
  const response = await apiClient.post<UpdateAccountExtensionStatusResponse>(
    '/account-extensions/status',
    {
      ...request,
      trigger_data_sync: request.trigger_data_sync ?? true,
    },
    {
      timeout: 60000, // 1 minute for batch operations
    }
  );
  return response.data;
}
